import type { DomainOffersEntry } from '@/pages/useCalculateOffers';
import { inventoryApi } from '@/services/inventory-api.service';
import {
  getCheckoutSession,
  ordersService,
  type CheckoutOrderDetailsDto,
} from '@/services/orders.service';
import { useBookSkiPassStore, type SkierCounts } from '@/store/book-ski-pass.store';
import { offerCategoryMatchesOverride } from '@/utils/offer-category-match';
import { getPassQuantityKey } from '@/utils/pass-quantity-key';
import { useCallback, useEffect, useRef, useState } from 'react';

function debugPassSyncLog(event: string, payload: Record<string, unknown>): void {
  if (typeof globalThis === 'undefined') return;
  const debugEnabled = Boolean((globalThis as { __B2C_DEBUG_PASS_SYNC?: boolean }).__B2C_DEBUG_PASS_SYNC);
  if (!debugEnabled) return;
  console.info(`[pass-sync][hydrate] ${event}`, payload);
}

export function hydratePassSelectionsFromOrder(
  order: CheckoutOrderDetailsDto,
  domains: DomainOffersEntry[]
): void {
  if (domains.length === 0) return;

  const store = useBookSkiPassStore.getState();
  if (store.stepThreeFormFieldsValues.passSelectionOverrides != null) return;

  debugPassSyncLog('start', {
    orderId: order.orderId,
    domains: domains.length,
    selections: (order.passSelections ?? []).length,
  });

  // Reconcile quantities for all visible pass offers so stale categories are cleared.
  for (const { domain, offers } of domains) {
    const domainPassItems = offers.items
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => item.type === 'pass');

    const domainSelections = (order.passSelections ?? []).filter(
      (selection) => selection.domain.id === domain.pk
    );

    const assignedByItemIndex = new Map<number, number>();
    let matchedSelectionsCount = 0;

    const addAssignedQty = (itemIndex: number, qty: number) => {
      if (qty <= 0) return;
      assignedByItemIndex.set(itemIndex, (assignedByItemIndex.get(itemIndex) ?? 0) + qty);
    };

    for (const selection of domainSelections) {
      const qty = Math.max(0, typeof selection.quantity === 'number' ? selection.quantity : 0);
      if (qty <= 0) continue;

      const matchedByPassAndCategory =
        selection.passOfferId != null
          ? domainPassItems.find(
              ({ item }) =>
                item.pass_pk === selection.passOfferId &&
                offerCategoryMatchesOverride(item.customer_type ?? '', selection.ageCategory)
            )
          : undefined;

      if (matchedByPassAndCategory) {
        matchedSelectionsCount += 1;
        addAssignedQty(matchedByPassAndCategory.idx, qty);
        continue;
      }

      const matchedByPassOnly =
        selection.passOfferId != null
          ? (() => {
              const passPkMatches = domainPassItems.filter(
                ({ item }) => item.pass_pk === selection.passOfferId
              );
              return passPkMatches.length === 1 ? passPkMatches[0] : undefined;
            })()
          : undefined;

      if (matchedByPassOnly) {
        matchedSelectionsCount += 1;
        addAssignedQty(matchedByPassOnly.idx, qty);
        continue;
      }

      const matchedByCategory = domainPassItems.find(({ item }) =>
        offerCategoryMatchesOverride(item.customer_type ?? '', selection.ageCategory)
      );

      if (matchedByCategory) {
        matchedSelectionsCount += 1;
        addAssignedQty(matchedByCategory.idx, qty);
      }
    }

    if (domainSelections.length > 0 && matchedSelectionsCount === 0) {
      debugPassSyncLog('domain.skip.no-match', {
        orderId: order.orderId,
        domainId: domain.pk,
        selections: domainSelections.length,
      });
      continue;
    }

    debugPassSyncLog('domain.apply', {
      orderId: order.orderId,
      domainId: domain.pk,
      selections: domainSelections.length,
      matchedSelectionsCount,
    });

    for (const { item, idx } of domainPassItems) {
      const qty = assignedByItemIndex.get(idx) ?? 0;
      store.setPassQuantity(domain.pk, qty, {
        passPk: item.pass_pk,
        customerType: item.customer_type,
        quantityKey: getPassQuantityKey(domain.pk, item, idx),
        itemIndex: idx,
        source: 'checkout:hydrate',
      });
    }
  }
}

function normalizeSkierCounts(counts: { category: string; quantity: number }[]): SkierCounts {
  const base: SkierCounts = {};
  counts.forEach((entry) => {
    const key = entry.category.trim();
    if (key) base[key] = entry.quantity;
  });
  return base;
}

async function hydrateTripDetailsToStore(order: CheckoutOrderDetailsDto): Promise<void> {
  const store = useBookSkiPassStore.getState();
  const { referenceResponseData, referenceResortDomains, selectedDomain } = store;

  if (order.reference != null) {
    store.updateStepOneFormField('bookingReference', order.reference);
    store.setBookingReferenceNumber(order.reference);
  }
  if (order.promoCode) {
    store.updateStepOneFormField('promoCode', order.promoCode);
  }

  const trip = order.tripDetails;
  if (!trip) return;

  const startDate = trip.startDate ? trip.startDate.split('T')[0] : '';
  store.updateStepTwoFormFields({
    skierCounts: normalizeSkierCounts(trip.skierCounts),
    daysOfSkiing: String(trip.skiDays),
    startingDate: startDate,
    skiersLevel: trip.skiersLevel ?? null,
    skiStyle: trip.skiStyle ?? null,
  });

  if (trip.domain) {
    const hasDomainsFromReference = Boolean(referenceResponseData?.domains?.length);
    const shouldFallbackToSingleTripDomain =
      !hasDomainsFromReference && !referenceResortDomains?.length;

    if (shouldFallbackToSingleTripDomain) {
      store.setReferenceResortDomains([trip.domain]);
    }

    if (!selectedDomain || selectedDomain.pk !== trip.domain.id) {
      const fullDomain = await inventoryApi.getDomainById(trip.domain.id);
      store.setSelectedDomain(fullDomain);
      store.captureReferenceDomainSeasonSnapshot(fullDomain);
    }
  }
}

function hydrateStepThreeToStore(order: CheckoutOrderDetailsDto): void {
  const store = useBookSkiPassStore.getState();

  if (Array.isArray(order.items) && order.items.length > 0) {
    const formsFromItems = order.items.map((item) => ({
      skipassId: item.skipassId,
      firstName: item.firstName,
      lastName: item.lastName,
      birthDate: item.birthDate ? item.birthDate.slice(0, 10) : '',
    }));
    store.updateStepThreeFormFields({ skierForms: formsFromItems });
  }

  store.updateStepThreeFormFields({
    customerEmail: order.customerEmail || '',
    customerPhone: order.customerPhone || '',
  });
}

export type UseCheckoutHydrationOptions = {
  hydrateTripDetails?: boolean;
  hydrateStepThree?: boolean;
  requiredSession?: boolean;
  onError?: (error: string) => void;
};

export function useCheckoutHydration(options: UseCheckoutHydrationOptions = {}) {
  const {
    hydrateTripDetails = false,
    hydrateStepThree = false,
    requiredSession = false,
    onError,
  } = options;

  const [order, setOrder] = useState<CheckoutOrderDetailsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);

  const fetchCheckout = useCallback(async () => {
    const session = getCheckoutSession();
    if (!session) {
      setLoading(false);
      if (requiredSession) {
        const message = 'Checkout session not found';
        setError(message);
        onError?.(message);
      }
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const fetched = await ordersService.getCheckout(session.orderId, session.checkoutToken);
      setOrder(fetched);

      if (hydrateTripDetails) {
        await hydrateTripDetailsToStore(fetched);
      }
      if (hydrateStepThree) {
        hydrateStepThreeToStore(fetched);
      }

      return fetched;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load checkout';
      setError(message);
      onError?.(message);
      setOrder(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [hydrateTripDetails, hydrateStepThree, requiredSession, onError]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    void fetchCheckout();
  }, [fetchCheckout]);

  return { order, loading, error, refetch: fetchCheckout };
}
